import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { TableColumn } from "react-data-table-component";
import { SoulDataModel } from "../../data/models/Soul/SoulDataModel";

type SubmissionStatus = {
  label: string;
  color: "default" | "warning" | "success";
  sortOrder: number;
};

type CreateUsersColumnsParams = {
  onViewForm: (user: SoulDataModel) => void;
};

const getDateTimestamp = (value?: string | null) => {
  if (!value) return 0;

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getSubmittedForms = (user: SoulDataModel) => {
  const forms = user.questionnaireForms ?? [];
  const submittedForms = forms.filter(
    (item) =>
      item.submission?.json && item.submission?.id && item.latestForm?.json,
  );

  if (submittedForms.length > 0) {
    return submittedForms;
  }

  if (user.submission?.json && user.submission?.id && user.latestForm?.json) {
    return [
      {
        questionnaire: {
          id: user.latestForm.id,
          code: "FORMULARIO",
          name: "Formulario",
        },
        submission: user.submission,
        latestForm: user.latestForm,
      },
    ];
  }

  return [];
};

const getLatestSubmission = (user: SoulDataModel) => {
  const submissions = getSubmittedForms(user)
    .map((item) => item.submission)
    .filter(Boolean);

  return submissions.sort(
    (a, b) => getDateTimestamp(b?.updatedAt) - getDateTimestamp(a?.updatedAt),
  )[0];
};

const getSubmissionStatus = (user: SoulDataModel): SubmissionStatus => {
  const forms = getSubmittedForms(user);
  const submission = getLatestSubmission(user);

  if (
    forms.length > 0 &&
    forms.every(
      (item) =>
        item.submission?.finishedAt || item.submission?.status === "COMPLETED",
    )
  ) {
    return {
      label: forms.length > 1 ? `${forms.length} finalizados` : "Finalizado",
      color: "success",
      sortOrder: 2,
    };
  }

  if (submission?.json) {
    return {
      label: forms.length > 1 ? `${forms.length} en proceso` : "En proceso",
      color: "warning",
      sortOrder: 1,
    };
  }

  return {
    label: "No iniciado",
    color: "default",
    sortOrder: 0,
  };
};

const pillSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  px: 1.5,
  py: 0.5,
  borderRadius: 999,
  minWidth: 96,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.2,
};

const renderPlanPill = (label: string, filled: boolean) => (
  <Box
    sx={{
      ...pillSx,
      bgcolor: filled ? "success.main" : "#f5f5f5",
      color: filled ? "common.white" : "text.secondary",
    }}
  >
    {label}
  </Box>
);

export const createUsersColumns = ({
  onViewForm,
}: CreateUsersColumnsParams): TableColumn<SoulDataModel>[] => [
  {
    name: "Usuario",
    selector: (row) => row.fullName ?? "",
    sortable: true,
    wrap: true,
    cell: (row) => (
      <Typography fontWeight={600}>{row.fullName ?? "Anonimo"}</Typography>
    ),
  },
  {
    name: "Correo",
    selector: (row) => row.email ?? "",
    sortable: true,
    wrap: true,
    cell: (row) => <Typography>{row.email || "-"}</Typography>,
  },
  {
    name: "Rol",
    selector: (row) => row.role ?? "",
    sortable: true,
    width: "110px",
    cell: (row) => <Typography>{row.role || "-"}</Typography>,
  },
  {
    name: "Plan",
    selector: (row) => row.plan?.name ?? "",
    sortable: true,
    minWidth: "220px",
    cell: (row) =>
      row.plan
        ? renderPlanPill(row.plan.name, true)
        : renderPlanPill("Sin plan", false),
  },
  {
    name: "Pasaporte",
    selector: (row) => row.passport ?? "",
    sortable: true,
    width: "140px",
    cell: (row) => <Typography>{row.passport || "-"}</Typography>,
  },
  {
    name: "Registrado",
    selector: (row) => getDateTimestamp(row.createdAt),
    sortable: true,
    width: "140px",
    cell: (row) => (
      <Typography>
        {row.createdAt
          ? new Date(row.createdAt).toLocaleDateString("es-CO")
          : "-"}
      </Typography>
    ),
  },
  {
    name: "Formulario",
    selector: (row) => getSubmissionStatus(row).sortOrder,
    sortable: true,
    width: "160px",
    cell: (row) => {
      const status = getSubmissionStatus(row);

      return (
        <Chip
          label={status.label}
          color={status.color}
          size="small"
          sx={{
            fontWeight: 700,
            minWidth: 100,
            color:
              status.color === "default" ? "text.secondary" : "common.white",
          }}
        />
      );
    },
  },
  {
    name: "Actualizado",
    selector: (row) => getDateTimestamp(getLatestSubmission(row)?.updatedAt),
    sortable: true,
    width: "180px",
    cell: (row) => (
      <Typography>
        {getLatestSubmission(row)?.updatedAt
          ? new Date(getLatestSubmission(row)!.updatedAt!).toLocaleString(
              "es-CO",
            )
          : "-"}
      </Typography>
    ),
  },
  {
    name: "Finalizado",
    selector: (row) => getDateTimestamp(getLatestSubmission(row)?.finishedAt),
    sortable: true,
    width: "180px",
    cell: (row) => (
      <Typography>
        {getLatestSubmission(row)?.finishedAt
          ? new Date(getLatestSubmission(row)!.finishedAt!).toLocaleString(
              "es-CO",
            )
          : "-"}
      </Typography>
    ),
  },
  {
    name: "Accion",
    sortable: false,
    width: "110px",
    right: true,
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    cell: (row) => {
      const canOpenForm = getSubmittedForms(row).length > 0;

      if (!canOpenForm) {
        return null;
      }

      return (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="info"
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              minWidth: 56,
            }}
            onClick={() => onViewForm(row)}
          >
            Ver
          </Button>
        </Stack>
      );
    },
  },
];
